import { useRef, useState } from 'react';
import { ausAbwurf, ausFeld, type Baumdatei } from './ordnerabwurf.ts';

/**
 * Das Abwurffeld. DIE Stelle, an der Dateien in dieses System kommen.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „wenn ich dateien hochladen will, dann überall per drag and drop, nichts
 *    anderes"
 *
 * ES GAB VIER VERSCHIEDENE WEGE HEREIN, und jeder sah anders aus: ein
 * Abwurffeld mit einem Knopf daneben, ein Formular mit „Durchsuchen…", ein
 * nacktes Dateifeld in den Stammdaten, ein Ordnerwähler im Umzug. Vier
 * Bauarten für eine Handbewegung.
 *
 * JETZT EINE, UND SIE HAT ZWEI GESTEN:
 *
 *   ZIEHEN am Schreibtisch. Dateien, Ordner, ganze Bäume.
 *   TIPPEN am Telefon. Die Fläche SELBST ist der Knopf.
 *
 * DAS IST KEIN ZWEITER WEG, SONDERN DERSELBE. Es gibt kein Drag & Drop auf
 * einem Telefon - der Monteur vor der Grube hätte mit „nichts anderes"
 * wörtlich genommen gar keinen Weg mehr, und genau er ist der, der am meisten
 * hochlädt. Die Fläche nimmt beides an, es gibt aber nur EIN Ding auf dem
 * Bildschirm: keinen Knopf daneben, kein Formular dahinter, keine zweite
 * Stelle, an der man auch hätte klicken können.
 *
 * UND EIN ABWURF, DER NICHTS HERGIBT, SAGT ES. Ein Ordner steht nicht in
 * `dataTransfer.files`; bis zum 26.08.2026 ging dabei nichts hinaus und es kam
 * keine Meldung. Wer hier nichts bekommt, schreibt es hin.
 */
/**
 * DIE GÄNGIGEN FORMATE, als Vorfilter des Dateiwählers.
 *
 * Marvin, 02.09.2026: „der Upload der Dateien ist nur in den gängigen
 * Formaten möglich." Der Riegel sitzt im SERVER (`regeln/dateiformat.ts`,
 * am Inhalt erkannt); dieser Filter ist die Höflichkeit davor - der
 * Dateiwähler zeigt gleich nur, was hereindarf. HEIC steht dabei, weil der
 * Server es zu JPEG wandelt: ein iPhone-Foto soll sich auswählen lassen.
 */
/* VIDEOS GEHÖREN DAZU (.386). ORIGINALTEXT MARVIN (16.10.2026): „ich muss die
   möglichkeit haben videos auch hochzuladen". */
export const GAENGIG = '.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,.docx,.xlsx,.csv,.mp4,.mov,.m4v,.webm,image/*,video/*,application/pdf';

export default function Abwurf({
  titel, hinweis, ordner, accept, laeuft, laufText, nimm, klein,
}: {
  /** Die fette Zeile in der Mitte. */
  titel: string;
  /** Was darunter steht. Wohin es geht und was hineingehört. */
  hinweis?: React.ReactNode;
  /** Nimmt die Fläche auch ganze ORDNER an? Dann wählt der Tipp einen Ordner. */
  ordner?: boolean;
  /** Womit der Dateiwähler vorfiltert, z. B. ".docx,.pdf". */
  accept?: string;
  laeuft?: boolean;
  /** Was während des Hochladens in der Fläche steht. */
  laufText?: string;
  /** Was mit dem Abgeworfenen geschieht. MIT Pfaden, immer. */
  nimm: (stuecke: Baumdatei[]) => void;
  /** Eine flachere Fläche für Stellen, an denen sie nicht der Hauptweg ist. */
  klein?: boolean;
}) {
  const [drueber, setDrueber] = useState(false);
  const [leer, setLeer] = useState(false);
  const feld = useRef<HTMLInputElement>(null);

  function weiter(stuecke: Baumdatei[]) {
    if (!stuecke.length) { setLeer(true); return; }
    setLeer(false);
    nimm(stuecke);
  }

  return (
    <>
      <div
        className={'ablage anklickbar' + (drueber ? ' drueber' : '')
                 + (klein ? ' flach' : '') + (laeuft ? ' laeuft' : '')}
        /* DIE FLÄCHE IST DER KNOPF. `role` und `tabIndex` machen sie auch für
           die Tastatur erreichbar: wer sich mit der Tabulatortaste durch den
           Bildschirm bewegt, käme sonst an der einzigen Stelle nicht vorbei,
           an der etwas hereinkommt. */
        role="button"
        tabIndex={laeuft ? -1 : 0}
        aria-disabled={laeuft || undefined}
        onClick={() => { if (!laeuft) feld.current?.click(); }}
        onKeyDown={(e) => {
          if (laeuft) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault(); feld.current?.click();
          }
        }}
        onDragOver={(e) => { e.preventDefault(); if (!laeuft) setDrueber(true); }}
        onDragLeave={() => setDrueber(false)}
        onDrop={async (e) => {
          e.preventDefault(); setDrueber(false);
          if (laeuft) return;
          weiter(await ausAbwurf(e.dataTransfer));
        }}
      >
        {/* `webkitdirectory` gibt es in den Typen von React nicht; das Feld
            heisst im Browser trotzdem so und ist der einzige Weg, einen
            ganzen Ordner zu wählen. */}
        <input ref={feld} type="file" multiple accept={accept ?? GAENGIG}
               style={{ display: 'none' }}
               {...(ordner ? ({ webkitdirectory: '', directory: '' } as any) : {})}
               onChange={(e) => {
                 const x = ausFeld(e.target.files);
                 e.target.value = '';
                 weiter(x);
               }} />
        <b>{laeuft ? (laufText ?? 'Lädt hoch') : titel}</b>
        {!laeuft && hinweis && <p className="klein leise">{hinweis}</p>}
        {!laeuft && (
          <span className="klein leise tippzeile">
            Hierher ziehen oder tippen
          </span>
        )}
      </div>

      {/* DER SATZ, DER GEFEHLT HAT. Wenn ein Abwurf nichts hergibt, ist das
          nicht „nichts passiert", sondern eine Auskunft. */}
      {leer && (
        <div className="hinweis warnung" style={{ marginTop: 8 }}>
          <b>Aus dieser Ablage kam keine einzige Datei</b>
          Manche Browser geben abgelegte Ordner nicht heraus. Ein Tipp auf die
          Fläche öffnet die Auswahl und nimmt sie sicher.
        </div>
      )}
    </>
  );
}
